import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	OnDestroy,
	OnInit,
	untracked,
} from '@angular/core';
import { PaginationService } from '@uc/utilities';
import { ClientFeatures } from '@uc/web/shared/data-models';
import { UniversitiesStore } from '../../data-access/+state/universities.store';
import { CopyGeneratorService } from '../../services/copy-generator.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UniversitiesService } from '../../services/universities.service';
import {
	ClearingEmptyStateComponent,
	ClearingUniversityCardComponent,
	SearchBarWithStudyLevelComponent,
	LevelSearchBarParams,
	MainHeaderComponent,
	UniversitiesSkeletonComponent,
	InfoClearingToggleComponent,
} from '@web/shared/ui';
import { NgClass } from '@angular/common';
import { AppliedFiltersBarComponent } from '../../components/applied-filters-bar/applied-filters-bar.component';
import { UniversitiesFilterComponent } from '../../components/university-search-filter/universities-filter.component';
import { toObservable } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';
import { UniversityFilterPill } from '../../models/university-search.models';
import { FilterTogglerService } from '@uc/web/core';
import { CounterService } from '@uc/web/analytics';
import { UniversitySearchScrollerService } from '../../services/university-search-scroller.service';
import {
	FilterDarkOverlayComponent,
	FilterSortWidgetComponent,
} from '@uc/web/shared/features/feature-filter';
import { INFO_CLEARING_TOGGLE } from '../../static/universities.properties';
import { NoResultComponent } from '@uc/shared/ui';
import { CLEARING_ENABLED } from '@uc/web/config';
import { NA, NullResultsGAService, UniversityGAService } from '@uc/web/analytics';
import { Product } from '@uc/shared/data-models';

@Component({
	selector: 'uc-clearing-university-search',
	templateUrl: './clearing-university-search.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		NgClass,
		ClearingUniversityCardComponent,
		AppliedFiltersBarComponent,
		UniversitiesFilterComponent,
		UniversitiesSkeletonComponent,
		SearchBarWithStudyLevelComponent,
		MainHeaderComponent,
		ClearingEmptyStateComponent,
		FilterDarkOverlayComponent,
		FilterSortWidgetComponent,
		InfoClearingToggleComponent,
		NoResultComponent,
	],
})
export class ClearingUniversitySearchComponent implements OnInit, OnDestroy {
	readonly store = inject(UniversitiesStore);
	readonly paidFeatures = [ClientFeatures.Clearing];
	product: Product = 'clearing';
	searchTerm = this.store.payload.name;
	page = this.store.payload.page;
	universities = this.store.clearingUniversities;

	headerData = this._unisSrv.headerData;

	universitiesFound = computed(() => {
		const uniCount = this.store.clearingUniversities()?.data.length || 0;
		return `${uniCount > 1 ? 'universities' : 'university'} found`;
	});

	firstLoad = true;
	clearingEnabled = CLEARING_ENABLED;
	queryParams!: Params;

	readonly infoClearingToggle = INFO_CLEARING_TOGGLE;
	readonly na = NA;

	private _filtersLoadedSub = toObservable(this.store.filtersLoaded)
		.pipe(
			tap((filtersLoaded) => {
				const queryParams = this._route.snapshot.queryParams;
				if (filtersLoaded) {
					this.store.initQueryParamsAndProduct(queryParams, this.product);
				}
			}),
		)
		.subscribe();

	constructor(
		private _route: ActivatedRoute,
		private _router: Router,
		private _unisSrv: UniversitiesService,
		private _nullResultsGASrv: NullResultsGAService,
		private _paginationSrv: PaginationService,
		private _uniGASrv: UniversityGAService,
		private _counterSrv: CounterService,
		private _scrollSrv: UniversitySearchScrollerService,
		private _copyGeneratorSrv: CopyGeneratorService,
		public filterTogglerSrv: FilterTogglerService,
	) {
		effect(() => {
			untracked(() => {
				this._copyGeneratorSrv.setHeaderData(
					this.store.product(),
					this._getLocation(this.store.pills()),
				);
			});
			this._copyGeneratorSrv.setSeoData(
				this.store.product(),
				this._getLocation(this.store.pills()),
			);

			const universities = this.store.clearingUniversities()?.data;

			if (!universities) return;

			const uniCount = universities.length || 0;
			this._paginationSrv.createPaginationSEO(this.page(), uniCount + 1);

			if (!universities?.length) {
				// GA Event
				this._nullResultsGASrv.nullResults(this.searchTerm() || '');
			}

			this.firstLoad = false;
		});
	}

	ngOnInit(): void {
		this.store.fetchFilterLocations();
	}

	ngOnDestroy(): void {
		this._uniGASrv.uniListInViewPort(this._counterSrv.count);
		this._counterSrv.reset();
		this._paginationSrv.remove();
		this._filtersLoadedSub.unsubscribe();
		this.store.resetStore();
	}

	onStudyLevelSelect(product: Product) {
		this._unisSrv.onStudyLevelSelect(product);
	}

	async onSearch(params: LevelSearchBarParams) {
		const { searchTerm, product } = params;

		this.store.updateProduct(product);
		this.store.updateSearchTerm(searchTerm);
		const segments = ['/universities', product];

		await this._router.navigate(segments, {
			queryParams: this.store.queryParams(),
		});

		this._scrollSrv.scroll();
	}

	private _getLocation(pills: UniversityFilterPill[] | null) {
		const location = pills ? pills.filter((pill) => pill.urlKey === 'loc')[0] : null;
		const locationName = location ? location.label : '';
		return locationName;
	}
}
